import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { MomentCarouselItem, MomentCommerceIntro } from "../../data/momentOptions";
import { createMomentCoupon } from "../../data/momentOptions";

type Props = {
  commerceIntroEnabled: boolean;
  commerceIntro: MomentCommerceIntro;
  carouselItems: MomentCarouselItem[];
  shareFlag: boolean;
  moreButtonEnabled: boolean;
};

export default function CarouselCommercePreview({ commerceIntroEnabled, commerceIntro, carouselItems, shareFlag, moreButtonEnabled }: Props) {
  const visibleItems = carouselItems.length > 0 ? carouselItems.slice(0, 6) : [
    { id: "p1", messageTitle: "상품명 1", description: "상세 내용", priceAmount: "0", discountedPriceAmount: "0", imagePreview: null, imageName: null, imageFile: null, imageUploadedUrl: "", pcLandingUrl: "", mobileLandingUrl: "", buttonTitle: "", buttonPcLandingUrl: "", buttonMobileLandingUrl: "", secondaryButtonTitle: "", secondaryButtonPcLandingUrl: "", secondaryButtonMobileLandingUrl: "", secondaryButtonEnabled: false, shareFlag: false, couponEnabled: false, coupon: createMomentCoupon() },
    { id: "p2", messageTitle: "상품명 2", description: "상세 내용", priceAmount: "0", discountedPriceAmount: "0", imagePreview: null, imageName: null, imageFile: null, imageUploadedUrl: "", pcLandingUrl: "", mobileLandingUrl: "", buttonTitle: "", buttonPcLandingUrl: "", buttonMobileLandingUrl: "", secondaryButtonTitle: "", secondaryButtonPcLandingUrl: "", secondaryButtonMobileLandingUrl: "", secondaryButtonEnabled: false, shareFlag: false, couponEnabled: false, coupon: createMomentCoupon() },
  ];

  const totalSlides = visibleItems.length + (moreButtonEnabled ? 1 : 0) + (commerceIntroEnabled ? 1 : 0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartXRef = useRef<number | null>(null);

  const formatPrice = (value: string) => `${value || "0"}원`;
  const getDiscountRate = (priceAmount: string, discountedPriceAmount: string) => {
    const original = Number(priceAmount);
    const discounted = Number(discountedPriceAmount);

    if (!Number.isFinite(original) || !Number.isFinite(discounted) || original <= 0 || discounted < 0 || discounted >= original) {
      return null;
    }

    return Math.floor(((original - discounted) / original) * 100);
  };

  useEffect(() => {
    if (currentIndex > Math.max(totalSlides - 1, 0)) {
      setCurrentIndex(Math.max(totalSlides - 1, 0));
    }
  }, [currentIndex, totalSlides]);
  const slideWidth = 240;
  const gap = 8;
  const viewportWidth = 272;
  const moreWidth = moreButtonEnabled ? 72 : 0;
  const normalOffset = currentIndex * (slideWidth + gap);
  const fullTrackWidth =
    (commerceIntroEnabled ? slideWidth + gap : 0) +
    visibleItems.length * slideWidth +
    Math.max(visibleItems.length - 1, 0) * gap +
    (moreButtonEnabled ? gap + moreWidth : 0);
  const endAlignedOffset = Math.max(fullTrackWidth - viewportWidth, 0);
  const trackOffset =
    moreButtonEnabled && currentIndex === totalSlides - 1
      ? endAlignedOffset
      : normalOffset;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = e.clientX;
    setDragOffset(0);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartXRef.current === null) return;
    setDragOffset(e.clientX - dragStartXRef.current);
  };

  const handlePointerEnd = () => {
    if (dragStartXRef.current === null) return;
    if (dragOffset <= -40) {
      setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1));
    } else if (dragOffset >= 40) {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
    dragStartXRef.current = null;
    setDragOffset(0);
  };

  return (
    <div className="tw-text-[13px] tw-leading-relaxed">
      <div
        className="tw-py-2 tw-w-[272px] tw-overflow-hidden tw-cursor-grab active:tw-cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div
          className="tw-flex tw-gap-2 tw-transition-transform tw-duration-300 tw-ease-out"
          style={{ transform: `translateX(${-trackOffset + dragOffset}px)` }}
        >
          {commerceIntroEnabled && (
            <div className="tw-w-60 tw-shrink-0 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-shadow-sm">
              <div className="tw-aspect-square tw-bg-gray-50 tw-relative">
                {commerceIntro.imagePreview && (
                  <img src={commerceIntro.imagePreview} alt="" draggable={false} className="tw-w-full tw-h-full tw-object-cover tw-select-none" />
                )}
              </div>
              <div className="tw-p-3">
                <h4 className="tw-font-medium tw-text-sm tw-mb-1 tw-whitespace-pre-line tw-break-all">
                  {commerceIntro.messageTitle || "인트로 타이틀"}
                </h4>
                <div className="tw-text-xs tw-text-gray-600 tw-whitespace-pre-line tw-break-all">
                  {commerceIntro.description || "인트로 홍보 문구"}
                </div>
              </div>
            </div>
          )}
          {visibleItems.map((item) => (
            <div key={item.id} className="tw-w-60 tw-shrink-0 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-shadow-sm">
              <div className="tw-aspect-square tw-bg-gray-50 tw-relative">
                {item.imagePreview && (
                  <img src={item.imagePreview} alt="" draggable={false} className="tw-w-full tw-h-full tw-object-cover tw-select-none" />
                )}
              </div>
              <div className="tw-p-3">
                <h4 className="tw-font-medium tw-text-sm tw-mb-1 tw-whitespace-pre-line tw-break-all">
                  {item.messageTitle || "상품명"}
                </h4>
                {item.discountedPriceAmount && Number(item.discountedPriceAmount) > 0 ? (
                  <div className="tw-flex tw-items-baseline tw-gap-1">
                    <span className="tw-text-gray-900 tw-font-bold tw-text-sm">
                      {formatPrice(item.discountedPriceAmount)}
                    </span>
                    <span className="tw-text-gray-400 tw-text-[10px] tw-line-through">
                      {formatPrice(item.priceAmount)}
                    </span>
                    {getDiscountRate(item.priceAmount, item.discountedPriceAmount) !== null && (
                      <span className="tw-text-red-500 tw-text-[10px] tw-font-semibold">
                        {getDiscountRate(item.priceAmount, item.discountedPriceAmount)}%
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="tw-flex tw-items-baseline tw-gap-1">
                    <span className="tw-text-gray-900 tw-font-bold tw-text-sm">
                      {formatPrice(item.priceAmount)}
                    </span>
                  </div>
                )}
                <div className="tw-border-t tw-border-gray-100 tw-my-2" />
                <div className="tw-text-xs tw-text-gray-600 tw-whitespace-pre-line tw-break-all">
                  {item.description || "상세 내용"}
                </div>
                <div className={`tw-mt-3 ${shareFlag ? "tw-flex tw-gap-1" : ""}`}>
                  <div className={`${shareFlag ? "tw-flex-1" : "tw-w-full"} tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded tw-text-[10px] tw-text-center tw-font-bold tw-text-gray-700`}>
                    구매하기
                  </div>
                  {shareFlag && (
                    <div className="tw-flex-1 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded tw-text-[10px] tw-text-center tw-font-bold tw-text-[#3a1d96]">
                      공유하기
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {moreButtonEnabled && (
            <div className="tw-w-[72px] tw-shrink-0 tw-h-[237px] tw-flex tw-items-center tw-justify-center">
              <button
                type="button"
                className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-border-0 tw-bg-transparent tw-p-0 tw-shadow-none tw-outline-none tw-ring-0 tw-text-gray-600"
              >
                <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-400">
                  <ArrowRight className="tw-h-5 tw-w-5 tw-text-white" />
                </span>
                <span className="tw-text-sm tw-font-semibold tw-text-gray-500">더보기</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {totalSlides > 1 && (
        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-w-60">
          <button
            type="button"
            onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}
            disabled={currentIndex === 0}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-600 disabled:tw-opacity-40"
          >
            <ArrowLeft className="tw-w-3.5 tw-h-3.5" />
            이전
          </button>
          <span className="tw-text-xs tw-text-gray-500">
            {currentIndex + 1} / {totalSlides}
          </span>
          <button
            type="button"
            onClick={() => setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1))}
            disabled={currentIndex === totalSlides - 1}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-medium tw-text-gray-600 disabled:tw-opacity-40"
          >
            다음
            <ArrowRight className="tw-w-3.5 tw-h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
